// Wallpaper project math: rooms → rolls, accounting for pattern repeat.
// Pattern drop adds extra length needed per strip; rolls calculated from strips.
import { toFeet, wallAreaFromRoom, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

// 'dimensions' = user enters L × W × H + openings.
// 'area' = user already knows the net wall area and types it directly.
// Ceiling height stays required in both modes because strip length needs it.
export type WallpaperInputMode = 'dimensions' | 'area'

export interface WallpaperInputs {
  unit: UnitSystem
  inputMode: WallpaperInputMode
  length: number
  width: number
  ceilingHeight: number
  doors: number              // deduct 20 sq ft each
  windows: number            // deduct 15 sq ft each
  totalArea: number          // sq ft (imperial) or m² (metric) — used in 'area' mode
  rollWidthIn: number        // inches, default 20.5 (Euro standard)
  rollLengthFt: number       // feet, default 33
  patternRepeatIn: number    // inches, 0 = no repeat
  wastePct: number           // 10-25, default 15
}

export interface WallpaperResult {
  wallArea: number           // sq ft net (after deductions, before waste)
  wallAreaWithWaste: number  // sq ft after waste
  strips: number             // total strips needed (ceiling height + pattern drop)
  stripsPerRoll: number      // how many strips fit in one roll
  rolls: number              // rolls to buy
  pasteBuckets: number       // 1 per 4 rolls
  primerGallons: number      // optional: 1 gal per 100 sq ft
  lines: AccessoryLine[]
}

export const DEFAULT_WALLPAPER: WallpaperInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  length: 12,
  width: 10,
  ceilingHeight: 9,
  doors: 1,
  windows: 1,
  totalArea: 361,            // 2*(12+10)*9 - 20 - 15 = 361 sq ft (matches default)
  rollWidthIn: 20.5,
  rollLengthFt: 33,
  patternRepeatIn: 0,
  wastePct: 15,
}

export function computeWallpaper(i: WallpaperInputs, locale: Locale = 'en'): WallpaperResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const hFt = toFeet(i.ceilingHeight, i.unit)

  let wallArea: number
  if (i.inputMode === 'area') {
    // User-entered net wall area — already minus doors/windows.
    wallArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
  } else {
    const lFt = toFeet(i.length, i.unit)
    const wFt = toFeet(i.width, i.unit)
    wallArea = Math.max(0,
      wallAreaFromRoom(wFt, lFt, hFt)
      - Math.max(0, i.doors) * 20
      - Math.max(0, i.windows) * 15
    )
  }
  const wallAreaWithWaste = applyWaste(wallArea, i.wastePct)

  // Strip width in feet
  const rollWidthFt = Math.max(0.5, i.rollWidthIn) / 12

  // Strip length = ceiling height plus pattern drop (round ceiling height up to nearest repeat)
  const patternRepeatFt = Math.max(0, i.patternRepeatIn) / 12
  const stripLengthFt = patternRepeatFt > 0
    ? hFt + patternRepeatFt  // add one full repeat for pattern matching
    : hFt

  // Strips that fit in one roll (floor, not ceil — partial strips not usable for full-height)
  const rollLengthFt = Math.max(1, i.rollLengthFt)
  const stripsPerRoll = Math.max(1, Math.floor(rollLengthFt / stripLengthFt))

  // Total strips needed from area
  const stripsNeeded = wallArea > 0
    ? ceilTo(wallAreaWithWaste / (rollWidthFt * stripLengthFt), 1)
    : 0

  const rolls = stripsNeeded > 0 ? ceilTo(stripsNeeded / stripsPerRoll, 1) : 0

  const pasteBuckets = rolls > 0 ? Math.max(1, ceilTo(rolls / 4, 1)) : 0
  const primerGallons = wallArea > 0 ? Math.max(1, ceilTo(wallArea / 100, 1)) : 0

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'wallpaper paste adhesive bucket',
    label: tr('Wallpaper paste', 'Colle à papier peint'),
    kind: 'consumable',
    qty: pasteBuckets,
    unit: 'bucket',
    note: tr(`1 bucket per 4 rolls · use the paste type specified on the wallpaper label (standard or heavy-duty)`, `1 seau par 4 rouleaux · utilisez le type de colle indiqué sur l’étiquette du papier peint (standard ou robuste)`),
  })

  lines.push({
    item: 'wallpaper seam roller tool',
    label: tr('Seam roller', 'Roulette à joints'),
    kind: 'tool',
    note: tr('Firmly press seams for invisible joints — do not press embossed or textured wallpaper.', 'Pressez fermement les joints pour des raccords invisibles — ne pressez pas le papier peint gaufré ou texturé.'),
  })

  lines.push({
    item: 'wallpaper smoother brush squeegee',
    label: tr('Wallpaper smoother', 'Spatule à maroufler'),
    kind: 'tool',
    note: tr('Plastic smoother or wide brush to eliminate bubbles while hanging.', 'Spatule en plastique ou large brosse pour chasser les bulles pendant la pose.'),
  })

  lines.push({
    item: 'wallpaper primer sizing coat interior',
    label: tr('Wallpaper primer / sizing', 'Apprêt / fixateur à papier peint'),
    kind: 'consumable',
    qty: primerGallons,
    unit: 'gal',
    note: tr(`~100 sq ft per gallon · sizing primer prevents paste soaking into drywall and makes removal easier`, `~100 pi² par gallon · le fixateur empêche la colle de pénétrer dans le gypse et facilite le décollage`),
    optional: true,
  })

  lines.push({
    item: 'wallpaper scoring tool perforating',
    label: tr('Scoring / perforating tool', 'Outil à perforer'),
    kind: 'tool',
    note: tr('Scores old wallpaper before applying stripper — not needed for new installs.', 'Perfore le vieux papier peint avant l’application du décapant — inutile pour une nouvelle pose.'),
    optional: true,
  })

  lines.push({ item: 'wallpaper cutting tool wide blade craft knife', label: tr('Wide-blade craft knife', 'Couteau de précision à large lame'), kind: 'tool', note: tr('Sharp blades are critical — swap frequently for clean cuts.', 'Des lames bien affûtées sont essentielles — changez-les souvent pour des coupes nettes.') })
  lines.push({ item: 'wallpaper water tray soaking trough', label: tr('Soaking trough (pre-pasted)', 'Bac à tremper (papier préencollé)'), kind: 'tool', note: tr('Required only for pre-pasted wallpaper.', 'Nécessaire seulement pour le papier peint préencollé.'), optional: true })

  return {
    wallArea: round(wallArea, 0),
    wallAreaWithWaste: round(wallAreaWithWaste, 0),
    strips: stripsNeeded,
    stripsPerRoll,
    rolls,
    pasteBuckets,
    primerGallons,
    lines,
  }
}
